/**
 * StructuredData Component
 * 
 * React component that injects Schema.org JSON-LD structured data into the page head.
 * This is critical for SEO as it helps search engines understand your content.
 * 
 * Benefits:
 * - Rich snippets in Google search results (stars, prices, FAQs)
 * - Featured snippets and "People also ask" boxes
 * - Better understanding by search engines
 * - Voice search optimization
 * 
 * Usage:
 * ```tsx
 * <StructuredData schema={yourSchemaObject} />
 * ```
 * 
 * Or for multiple schemas:
 * ```tsx
 * <StructuredData schemas={[schema1, schema2, schema3]} />
 * ```
 * 
 * Testing:
 * - Google Rich Results Test: https://search.google.com/test/rich-results
 * - Schema.org Validator: https://validator.schema.org/
 */

import { Helmet } from 'react-helmet-async';
import type { AnySchema, JsonLd } from '@/types/schema';
import { isValidSchema, validateSchema } from '@/types/schema';

interface StructuredDataProps {
  /** Single schema object */
  schema?: AnySchema;
  /** Multiple schema objects */
  schemas?: AnySchema[];
  /** Enable validation warnings in development */
  validate?: boolean;
}

/**
 * StructuredData Component
 * 
 * Renders JSON-LD structured data in the document head using react-helmet-async.
 * Validates schemas in development mode to catch errors early.
 */
export function StructuredData({
  schema,
  schemas,
  validate = true,
}: StructuredDataProps) {
  // Normalize to array
  const schemaList = schemas || (schema ? [schema] : []);

  // Validate schemas in development
  if (validate && import.meta.env.DEV) {
    schemaList.forEach((s, index) => {
      if (!isValidSchema(s)) {
        console.warn(`[StructuredData] Invalid schema at index ${index}:`, s);
        return;
      }

      const validation = validateSchema(s);
      if (!validation.valid) {
        console.warn(
          `[StructuredData] Schema validation failed for ${s['@type']}:`,
          validation.errors
        );
      }
    });
  }

  // If no valid schemas, don't render anything
  if (schemaList.length === 0) {
    return null;
  }

  return (
    <Helmet>
      {schemaList.map((s, index) => (
        <script
          key={`structured-data-${s['@type']}-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(s, null, import.meta.env.DEV ? 2 : 0),
          }}
        />
      ))}
    </Helmet>
  );
}

/**
 * Hook to generate structured data dynamically
 * Useful when schema depends on component state or props
 */
export function useStructuredData(
  schemaGenerator: () => AnySchema | AnySchema[] | null
): AnySchema[] {
  const result = schemaGenerator();
  
  if (!result) return [];
  if (Array.isArray(result)) return result;
  return [result];
}

/**
 * Utility to combine multiple schemas
 * Useful when a page needs multiple types of structured data
 */
export function combineSchemas(...schemas: (AnySchema | AnySchema[] | null | undefined)[]): AnySchema[] {
  return schemas
    .flat()
    .filter((s): s is AnySchema => s !== null && s !== undefined && isValidSchema(s));
}

export default StructuredData;
